import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api.js';
import { formatIdrAbbreviated } from '../../lib/formatters.js';

interface SpeedKpis {
  medianSettlementDays: number;
  claimsInFlight: number;
  settledThisWeek: number;
  workingCapitalReleasedYtdIdr: number;
}

interface KpiTileProps {
  label: string;
  value: string;
  subtitle: string;
  accentClass: string;
  onClick?: () => void;
  tooltip?: string;
}

function KpiTile({ label, value, subtitle, accentClass, onClick, tooltip }: KpiTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      title={tooltip}
      className="bg-bone border border-hairline px-5 py-4 text-left hover:border-copper transition disabled:cursor-default disabled:hover:border-hairline"
    >
      <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">
        {label}
      </div>
      <div className={`text-4xl font-bold ${accentClass} mb-1 tabular-nums`}>{value}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </button>
  );
}

function SpeedKpiStripSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      {[0, 1, 2].map((i) => (
        <div key={i} className="bg-bone border border-hairline px-5 py-4 animate-pulse">
          <div className="h-2 w-24 bg-slate-100 rounded mb-3" />
          <div className="h-10 w-16 bg-slate-100 rounded mb-2" />
          <div className="h-2 w-32 bg-slate-100 rounded" />
        </div>
      ))}
    </div>
  );
}

export function SpeedKpiStrip() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<SpeedKpis | null>(null);

  useEffect(() => {
    function load() {
      api.get<SpeedKpis>('/approver/kpis/speed').then(setData).catch(() => {});
    }
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  if (!data) return <SpeedKpiStripSkeleton />;

  return (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <KpiTile
        label={t('approver.kpis.speed.medianDays')}
        value={data.medianSettlementDays.toString()}
        subtitle={t('approver.kpis.speed.medianDaysSubtitle')}
        accentClass="text-copper"
        onClick={() => navigate('/approver/settled?range=90d')}
      />
      <KpiTile
        label={t('approver.kpis.speed.inFlight')}
        value={data.claimsInFlight.toString()}
        subtitle={t('approver.kpis.speed.inFlightSubtitle')}
        accentClass="text-ink"
        onClick={() => navigate('/approver/queue')}
      />
      <KpiTile
        label={t('approver.kpis.speed.ytdClaimsSettled')}
        value={formatIdrAbbreviated(data.workingCapitalReleasedYtdIdr)}
        subtitle=""
        accentClass="text-emerald-700"
        tooltip={t('approver.kpis.speed.ytdClaimsSettledTooltip')}
      />
    </div>
  );
}
